import { type FC } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import cn from '@/utils/classNames'
import Image from 'next/image'
import open from '../../../public/icon-open.svg'
import close from '../../../public/icon-close.svg'

import styles from './Accordion.module.scss'

/**
 * Accordion Component
 *
 * A vertically stacked set of interactive headings that each reveal a section of content.
 *
 * Usage Example:
 * ```tsx
 * <Accordion type="single" collapsible>
 *   <Accordion.Item value="item-1">
 *     <Accordion.Trigger>Section 1</Accordion.Trigger>
 *     <Accordion.Content>Content for section 1</Accordion.Content>
 *   </Accordion.Item>
 * </Accordion>
 * ```
 */
const Accordion: FC<AccordionProps> = (props) => {
  const { className, children, ...rest } = props
  const classList = cn(styles.accordion, className)

  return (
    <AccordionPrimitive.Root className={classList} {...rest}>
      {children}
    </AccordionPrimitive.Root>
  )
}

/**
 * A wrapper component for each accordion section.
 */
const AccordionItem: FC<AccordionPrimitive.AccordionItemProps> = (props) => {
  const { className, ...rest } = props
  const classList = cn(styles.accordionItem, className)

  return (
    <AccordionPrimitive.Item className={classList} {...rest} />
  )
}

/**
 * Interactive element that toggles the visibility of its associated content.
 */
const AccordionTrigger: FC<AccordionPrimitive.AccordionTriggerProps> = (props) => {
  const { className, children, ...rest } = props
  const classList = cn(styles.accordionTrigger, className)

  return (
    <AccordionPrimitive.Header className={styles.accordionHeader}>
      <AccordionPrimitive.Trigger className={classList} {...rest}>
        {children}
        <span className={styles.icon} aria-hidden="true">
          <Image src={open} className={styles.iconOpen} alt="Open" width={32} height={32} />
          <Image src={close} className={styles.iconClose} alt="Close" width={32} height={32} />
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

/**
 * The collapsible content panel associated with a trigger.
 */
const AccordionContent: FC<AccordionPrimitive.AccordionContentProps> = (props) => {
  const { className, ...rest } = props
  const classList = cn(styles.accordionContent, className)

  return (
    <AccordionPrimitive.Content className={classList} {...rest} />
  )
}

/**
 * AccordionProps – must include the `type` field as required by Radix
 */
export type AccordionProps = {
  className?: string
  children: React.ReactNode
} & (
  | (AccordionPrimitive.AccordionSingleProps & { type: 'single' })
  | (AccordionPrimitive.AccordionMultipleProps & { type: 'multiple' })
)

export default Object.assign(Accordion, {
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent
})
